import React from 'react';
import { useNavigate } from 'react-router';
import cs from 'classnames';
import { startCase } from 'lodash';

import BriefProductLinks from 'features/briefs/components/atoms/BriefProductLinks';
import CenteredSpinner from 'features/ui/CenteredSpinner';
import NotificationBanner from 'shared/components/atoms/NotificationBanner';
import { Box, Button, Text } from 'shared/components/display';
import { DefinitionList, ListDetail } from 'shared/components/molecules/DefinitionList';
import Richtext from 'shared/components/Richtext';
import { isRichtextEmpty } from 'shared/components/Richtext/shared/utils';

import { BRIEF_COPY } from 'shared/config/copy';
import { useCurrentUser } from 'shared/hooks/useCurrentUser';
import { intl } from 'shared/lib/intl';
import { BUTTON_VARIANTS } from 'shared/styles/button';
import COLORS from 'shared/styles/colors';
import { Brief } from 'shared/typings/briefs';
import { UserType } from 'shared/typings/user/enums';
import { LinkCreator } from 'shared/utilities/linkUtility';
import { formatCurrency } from 'shared/utilities/stringUtility';

import styles from './styles';

interface Props {
  brief: Brief;
  loading?: boolean;
  className?: string;
}

const TruncatedBriefView: React.FC<Props> = ({ brief, loading = false, className = '' }) => {
  const { isSuperadmin } = useCurrentUser();
  const navigate = useNavigate();

  const navigateToBrief = () => {
    const briefLink = LinkCreator.createLink({
      routeKey: 'VIEW_INDIVIDUAL_BRIEF',
      userType: UserType.ORG,
      variables: {
        briefId: brief?.id,
        organizationId: brief?.organizationId,
        accountId: brief?.accountId,
      },
    });
    navigate(briefLink);
  };

  const showProductSection = brief?.website?.length > 0;
  const showBriefOverview = !isRichtextEmpty(JSON.parse(brief?.description));
  const showCreatorRequirements = !isRichtextEmpty(JSON.parse(brief?.creatorRequirements));
  const showModules = brief?.modules?.length > 0;
  const showPricing = isSuperadmin;

  return (
    <Box css={styles} dataCy="truncated-brief-view" className={cs('truncated-brief-view', className)}>
      {loading ? (
        <CenteredSpinner />
      ) : (
        <DefinitionList className="truncated-brief-view__content" labelColor={COLORS.NEUTRAL800}>
          <ListDetail
            text="Brief Overview"
            labelClassName="truncated-brief-view__heading"
            hide={!showBriefOverview}
            showBottomSeparator
          >
            <Richtext value={JSON.parse(brief.description)} key={`${brief.id}-description`} readOnly />
          </ListDetail>
          <ListDetail
            text="Product / Service"
            hide={!showProductSection}
            labelClassName="truncated-brief-view__heading"
            showBottomSeparator
          >
            <BriefProductLinks website={brief.website} />
          </ListDetail>
          {showPricing ? (
            <>
              {showBriefOverview && <hr className="truncated-brief-view__hr" />}
              <NotificationBanner
                theme="info-alt"
                message={BRIEF_COPY.BANNER_INTERNAL_VISIBILITY}
                className="truncated-brief-view__banner"
              />
            </>
          ) : (
            <></>
          )}
          <ListDetail
            text="Pricing"
            hide={!showPricing}
            labelClassName="truncated-brief-view__heading"
            showBottomSeparator
          >
            <DefinitionList labelColor={COLORS.NEUTRAL800}>
              <ListDetail
                dataCy="truncated-brief-view__payout"
                text={BRIEF_COPY.LABEL_CREATOR_PAYOUT}
                labelClassName="truncated-brief-view__pricing-label"
                detailsClassName="truncated-brief-view__pricing-details"
              >
                {intl.formatMessage(
                  {
                    id: 'DETAIL_PERFORMANCE_PAYOUT',
                  },
                  {
                    payout: formatCurrency(brief.type === 0 ? 0 : brief.creatorPayout),
                    creativeOnly: brief.creativeOnly,
                  },
                )}
              </ListDetail>
              <ListDetail
                text={BRIEF_COPY.LABEL_ELITE_CREATOR_PAYOUT}
                labelClassName="truncated-brief-view__pricing-label"
                detailsClassName="truncated-brief-view__pricing-details"
              >
                {intl.formatMessage(
                  {
                    id: 'DETAIL_PERFORMANCE_PAYOUT',
                  },
                  {
                    payout: formatCurrency(brief.type === 0 ? 0 : brief.eliteCreatorPayout),
                    creativeOnly: brief.creativeOnly,
                  },
                )}
              </ListDetail>
              <ListDetail
                text={BRIEF_COPY.LABEL_CREATOR_APPROVAL_PRICING}
                labelClassName="truncated-brief-view__pricing-label"
                detailsClassName="truncated-brief-view__pricing-details"
              >
                {formatCurrency(brief.type === 0 ? 0 : brief.creatorApprovalPricing)}
              </ListDetail>
              <ListDetail
                text={BRIEF_COPY.LABEL_ELITE_CREATOR_APPROVAL_PRICING}
                labelClassName="truncated-brief-view__pricing-label"
                detailsClassName="truncated-brief-view__pricing-details"
              >
                {formatCurrency(brief.type === 0 ? 0 : brief.eliteCreatorApprovalPricing)}
              </ListDetail>
            </DefinitionList>
          </ListDetail>
          <ListDetail
            text="Creator Requirements"
            hide={!showCreatorRequirements}
            labelClassName="truncated-brief-view__heading"
            showBottomSeparator
          >
            <Richtext value={JSON.parse(brief.creatorRequirements)} key={`${brief.id}-creator-requirements`} readOnly />
          </ListDetail>
          <ListDetail text="Modules Overview" hide={!showModules} labelClassName="truncated-brief-view__heading">
            {brief.modules.map((mod) => (
              <React.Fragment key={mod.id}>
                <Text className="truncated-brief-view__module-label">
                  <span className="truncated-brief-view__module-order-label">Module {mod.moduleNum}: </span>
                  {mod.name}
                </Text>
                <ul className="truncated-brief-view__module-file-list">
                  {(mod.aspectRatios.length ? mod.aspectRatios : ['']).map((ratio, index) => (
                    <li key={index}>
                      {ratio && `${ratio} `}
                      {mod.fileType === 'gif' ? 'GIF' : startCase(mod.fileType)}
                    </li>
                  ))}
                </ul>
              </React.Fragment>
            ))}
          </ListDetail>
        </DefinitionList>
      )}
      <Button onClick={navigateToBrief} variant={BUTTON_VARIANTS.TERTIARY} className="truncated-brief-view__button">
        View Full Brief
      </Button>
    </Box>
  );
};

export default TruncatedBriefView;
